/** Карточка сделки — ровно то, что отдаёт `GET /api/deals/{id}/card`. */
import type { NavRef } from "@/nav/NavMenu";
import { apiFetch } from "@/lib/api";
import type { ChatItem } from "@/lib/messenger";

export type EventKind =
  | "created" | "stage" | "call" | "chat" | "messenger" | "activity" | "comment"
  | "visit" | "fitting" | "contract" | "payment" | "move" | "heart";
/** Откуда событие: Bitrix24 (REST или своя копия) или CRM Prod. */
export type EventSrc = "bitrix" | "crm";

/** Товар в сердечках: фото, серия, статус «понравилось/нет», что с ним сделали. */
export type HeartItem = {
  image: string | null;
  name: string | null;
  serial: number;
  status: string | null;
  like: boolean;
  dislike: boolean;
  price: number;
  comment: string | null;
  deleted: boolean;
  note: string;
};
export type EventLink = "lead" | "deal" | "phone" | "chat" | "salon" | "crm";

export type CallInfo = {
  at: string;
  dir: "out" | "in" | "callback" | "auto" | "other";
  talk: boolean;
  sec: number;
  who: string | null;
  phone: string;
  link: EventLink;
  call_id: number | null;
  transcript: string | null;
  record: boolean;
};

export type ChatInfo = {
  id: number;
  client: string;
  answered: string | null;
  messages: { at: string; author: "client" | "operator" | "system"; name: string; text: string }[];
};

/** Блок переписки в WhatsApp / Telegram / MAX: один чат, паузы не дольше 3 ч. */
export type MessengerBlock = {
  chat_id: number;
  system: string | null;
  account: string | null;
  end: string;
  in: number;
  out: number;
  /** Как чат связан со сделкой: sMessenger_contact, sPhone, номер = id чата, привязка. */
  how: string[];
  items: ChatItem[];
};
export type ContactRow = {
  phone: string;
  bitrix: { id: number; name: string }[];
  lead: { id: number }[];
  contract: { id: number }[];
  customer: { id: number; name: string | null }[];
  places: number;
  lonely: boolean;
};

export type CardEvent = {
  at: string;
  kind: EventKind;
  title: string;
  src: EventSrc;
  who?: string | null;
  text?: string | null;
  /** Не слова человека: запись CRM о правке полей сделки («… изменил Начало проката…»). */
  auto?: boolean;
  link?: EventLink;
  done?: boolean;
  lines?: string[];
  /** Подозрительные правки договора: удаление/снижение цены/даты в день выдачи и позже. */
  alerts?: string[];
  gallery?: HeartItem[];
  call?: CallInfo;
  chat?: ChatInfo;
  messenger?: MessengerBlock;
  /** Объекты CRM события — меню переходов как смарт-кнопки table3 (`@/nav/NavMenu`). */
  refs?: NavRef[];
};

export type DealCard = {
  deal: {
    id: number;
    title: string | null;
    stage: string | null;
    /** Код стадии Bitrix (`3`, `PREPAYMENT_INVOICE`…) — для переключателя стадий. */
    stage_code: string | null;
    /** Направление Bitrix `CATEGORY_ID`: 0 — «Прокат»; город и мероприятие правим только у него. */
    category?: number;
    assigned: string | null;
    created: string | null;
    sum: string | null;
    /** Запись («Ждем клиента») или следующая обработка — `waitShowroom`, живой Bitrix первым. */
    visit: string | null;
    /** Прокат по сделке `ГГГГ-ММ-ДД` (поля Bitrix `rentStart`/`rentEnd`), живой Bitrix первым. */
    rent_start: string | null;
    rent_end: string | null;
    contact_id: number | null;
    /** Город сделки из CRM (`b24Deal.locationID`): «Москва» / «МСК»; сделки в CRM ещё нет — `null`. */
    city: string | null;
    city_short: string | null;
    /** Значение поля «салон» в Bitrix (`sPlace_locationID.dealLocationID`). */
    location_bitrix_id: number | null;
    /** Мероприятие — значение списка Bitrix (`StageSet.events`) и его дата `ГГГГ-ММ-ДД`. */
    event_id: number | null;
    event_date: string | null;
  };
  lead: {
    id: number;
    title?: string | null;
    source?: string | null;
    status?: string | null;
    /** `STATUS_ID` портала — для переключателя статуса (`LeadStatusSwitch`). */
    status_code?: string | null;
    created?: string | null;
    assigned?: string | null;
  } | null;
  phones: string[];
  summary: {
    calls_out: number;
    calls_out_talk: number;
    calls_in_talk: number;
    calls_by_phone: number;
    chats: number;
    messenger_chats: number;
    messenger_msgs: number;
    first_contact: string | null;
  };
  salon: {
    visits: number;
    repeat_visits: number;
    fittings_planned: number;
    contract: string | null;
    contract_alerts: number;
    hearts: number;
    hearts_liked: number;
    paid: number;
    issued: number;
    returned: number;
    /** Фактический прокат по `sMove_deal`: первая выдача и возврат последнего прокатного товара. */
    fact_start: string | null;
    fact_end: string | null;
    /** Прокат по договору CRM `ГГГГ-ММ-ДД` — сверить с датами сделки Bitrix. */
    contract_start: string | null;
    contract_end: string | null;
  } | null;
  events: CardEvent[];
  contacts: ContactRow[];
  errors: string[];
};

export function loadDealCard(id: number): Promise<DealCard> {
  return apiFetch<DealCard>(`/api/deals/${id}/card`);
}
